import React, { useState, useEffect, useContext, useMemo } from 'react';
import { X, Check, ChevronDown } from 'lucide-react';
import { DevicesContext } from '../../../contexts/DevicesContext';
import { ShareLinkDto } from '../../../services/shareLinkApi';
import '../../../styles/pages/alerts.css';
import '../../../styles/pages/share-link-additional.css';

interface EditShareLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (_data: { name: string; deviceIds: number[]; parameters?: string[] }) => Promise<void>;
  shareLinkData: ShareLinkDto;
}

const EditShareLinkModal: React.FC<EditShareLinkModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  shareLinkData,
}) => {
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('EditShareLinkModal must be used within a DevicesProvider');
  }
  const {
    devicesForDashboard,
    parametersForDashboard,
    getDevicesForDashboard,
    getParametersForDashboard,
    loading: devicesLoading
  } = devicesContext;
  const [formData, setFormData] = useState({
    name: '',
    deviceIds: [] as number[],
    parameters: [] as string[], // Parameter IDs as strings
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showDevicesDropdown, setShowDevicesDropdown] = useState(false);
  const [showParametersDropdown, setShowParametersDropdown] = useState(false);
  const [screenSize, setScreenSize] = useState(window.innerWidth);

  // Handle screen size changes
  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch devices and parameters when modal opens
  useEffect(() => {
    if (isOpen) {
      if (devicesForDashboard.length === 0) {
        getDevicesForDashboard();
      }
      if (parametersForDashboard.length === 0) {
        getParametersForDashboard();
      }
    }
  }, [isOpen, devicesForDashboard.length, parametersForDashboard.length, getDevicesForDashboard, getParametersForDashboard]);

  useEffect(() => {
    if (isOpen && shareLinkData) {
      // Extract device IDs from the details array
      const deviceIds = shareLinkData.details?.map(detail => detail.deviceId) || [];
      
      // Extract parameter IDs from the first detail (assuming same parameters for all devices)
      const parameterIds = shareLinkData.details && shareLinkData.details.length > 0
        ? shareLinkData.details[0].parameterIds.split(',').filter(p => p.trim())
        : [];
      
      // Validate device count on load
      const newErrors: { [key: string]: string } = {};
      if (deviceIds.length > 4) {
        newErrors.deviceIds = 'Maximum 4 devices can be selected for a share link. Please deselect some devices.';
      }
      
      setFormData({
        name: shareLinkData.shareLinkName,
        deviceIds: deviceIds, // Keep all devices but show error if more than 4
        parameters: parameterIds,
      });
      setErrors(newErrors);
      setShowDevicesDropdown(false);
      setShowParametersDropdown(false);
    }
  }, [isOpen, shareLinkData]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.form-dropdown-container')) {
        setShowDevicesDropdown(false);
        setShowParametersDropdown(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // For name field, validate in real-time
    if (name === 'name') {
      const trimmed = value.trim();
      if (!trimmed) {
        setErrors(prev => ({ ...prev, name: 'Share link name is required' }));
      } else if (trimmed.length < 4) {
        setErrors(prev => ({ ...prev, name: 'Share link name must be at least 4 characters' }));
      } else if (value.length > 30) {
        setErrors(prev => ({ ...prev, name: 'Share link name cannot exceed 30 characters' }));
      } else if (!/^[a-zA-Z0-9\s]+$/.test(value)) {
        setErrors(prev => ({ ...prev, name: 'Share link name can only contain letters, numbers and spaces' }));
      } else if (!/[a-zA-Z]/.test(trimmed)) {
        setErrors(prev => ({ ...prev, name: 'Share link name must contain at least one letter' }));
      } else if (/^(.)\1+$/.test(trimmed)) {
        setErrors(prev => ({ ...prev, name: 'Enter a valid share link name' }));
      } else if (/([a-zA-Z])\1{2,}/.test(value)) {
        setErrors(prev => ({ ...prev, name: 'Name cannot have a character repeated more than twice in a row' }));
      } else if (/[^aeiou0-9\s]{5,}/i.test(trimmed)) {
        setErrors(prev => ({ ...prev, name: 'Enter a valid share link name' }));
      } else {
        setErrors(prev => { const n = { ...prev }; delete n.name; return n; });
      }
    } else {
      // Clear error for other fields when user starts typing
      if (errors[name]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDeviceToggle = (deviceId: number) => {
    setFormData(prev => {
      const isCurrentlySelected = prev.deviceIds.includes(deviceId);

      // Deselection is always allowed; only validate when adding.
      if (!isCurrentlySelected) {
        // Cap share links at 4 devices.
        if (prev.deviceIds.length >= 4) {
          setErrors(prevErrors => ({
            ...prevErrors,
            deviceIds: 'Maximum 4 devices can be selected for a share link'
          }));
          return prev;
        }
      }

      // Clear the device-selection error on any valid change.
      setErrors(prevErrors => {
        if (!prevErrors.deviceIds) return prevErrors;
        const newErrors = { ...prevErrors };
        delete newErrors.deviceIds;
        return newErrors;
      });

      return {
        ...prev,
        deviceIds: isCurrentlySelected
          ? prev.deviceIds.filter(id => id !== deviceId)
          : [...prev.deviceIds, deviceId]
      };
    });
  };

  const handleParameterToggle = (parameterId: string) => {
    setFormData(prev => ({
      ...prev,
      parameters: prev.parameters.includes(parameterId)
        ? prev.parameters.filter(p => p !== parameterId)
        : [...prev.parameters, parameterId]
    }));
  };

  // Check if all parameters are selected
  const availableParameters = useMemo(() => 
    parametersForDashboard
      .filter(parameter => parameter.parameterName.toLowerCase() !== 'aqi')
      .map(parameter => parameter.driverID.toString()),
    [parametersForDashboard]
  );

  const allParametersSelected = useMemo(() => 
    availableParameters.length > 0 && 
    availableParameters.every(paramId => formData.parameters.includes(paramId)),
    [availableParameters, formData.parameters]
  );

  const handleSelectAllParameters = () => {
    const allSelected = availableParameters.every(paramId => 
      formData.parameters.includes(paramId)
    );
    
    setFormData(prev => ({
      ...prev,
      parameters: allSelected ? [] : [...availableParameters]
    }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Share link name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Share link name must be at least 3 characters';
    } else if (formData.name.trim().length > 50) {
      newErrors.name = 'Share link name must not exceed 50 characters';
    }

    if (formData.deviceIds.length === 0) {
      newErrors.deviceIds = 'Please select at least one device';
    } else if (formData.deviceIds.length > 4) {
      newErrors.deviceIds = 'Maximum 4 devices can be selected for a share link';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Additional safety check: ensure name doesn't exceed 50 characters
    const trimmedName = formData.name.trim();
    if (trimmedName.length > 50) {
      setErrors(prev => ({
        ...prev,
        name: 'Share link name must not exceed 50 characters'
      }));
      return;
    }

    setIsSubmitting(true);
    try {
      await onUpdate({
        name: trimmedName,
        deviceIds: formData.deviceIds,
        parameters: formData.parameters,
      });
    } catch (error) {
      console.error('Error updating share link:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay zoomOutLevel">
      <div className="modal-content edit-share-link-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="flex md:items-start items-center md:flex-col flex-row">
            <button onClick={onClose} className="md:hidden block mr-3">
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="back-icon"
              >
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>
            <h2 className="modal-title">Edit Share Link</h2>
            <p className="modal-subtitle md:block hidden">
              Edit the share link with devices and parameters.
            </p>
          </div>
          <button onClick={onClose} className="modal-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form className="modal-form sharelink-modal-form">
          {/* Share Link Name */}
          <div className="form-group">
            <label className="form-label">Share Link Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`form-input ${errors.name ? 'error' : ''}`}
              placeholder="Enter Share Link Name"
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="form-error">{errors.name}</p>
            )}
          </div>

          {/* Device Selection */}
          <div className="form-group">
            <label className="form-label">Devices</label>
            <div className="form-dropdown-container">
              <button
                type="button"
                className={`form-dropdown-button ${showDevicesDropdown ? 'active' : ''} ${errors.deviceIds ? 'error' : ''}`}
                onClick={() => setShowDevicesDropdown(!showDevicesDropdown)}
                disabled={isSubmitting}
              >
                <span className="form-dropdown-text">
                  {formData.deviceIds.length > 0 
                    ? `${formData.deviceIds.length} device${formData.deviceIds.length > 1 ? 's' : ''} selected`
                    : <span className='text-[#818C95]'>Select Devices</span>
                  }
                </span>
                <ChevronDown className="w-5 h-5 text-[var(--color-text)]" />
              </button>
              {showDevicesDropdown && (
                <>
                  {screenSize <= 768 && <div className="dropdown-overlay" onClick={() => setShowDevicesDropdown(false)}></div>}
                  <div className={`dropdown-menu ${screenSize <= 768 ? 'dropdown-menu-mobile' : ''}`}>
                    {screenSize <= 768 && (
                      <div className="mobile-modal-header">
                        <h3 className="mobile-modal-title">Select Devices</h3>
                        <button
                          type="button"
                          className="dropdown-menu-close"
                          onClick={() => setShowDevicesDropdown(false)}
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                    <div className="dropdown-menu-content">
                      {devicesLoading ? (
                        <div className="dropdown-loading">Loading devices...</div>
                      ) : devicesForDashboard.length === 0 ? (
                        <div className="dropdown-empty">No devices available</div>
                      ) : (
                        devicesForDashboard.map((device) => (
                          <button
                            key={device.deviceId}
                            type="button"
                            className="dropdown-item"
                            onClick={() => handleDeviceToggle(device.deviceId)}
                          >
                            <div className="dropdown-item-content">
                              <div className={`dropdown-checkbox ${formData.deviceIds.includes(device.deviceId) ? 'checked' : ''}`}>
                                {formData.deviceIds.includes(device.deviceId) && (
                                  <Check className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <div className="dropdown-item-text">
                                <span className="dropdown-item-name">{device.deviceName}</span>
                                <span className="dropdown-item-location">{device.installationLocation || 'No location set'}</span>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <p className="form-hint">Select at least one device</p>
            {errors.deviceIds && (
              <p className="form-error">{errors.deviceIds}</p>
            )}
          </div>

          {/* Parameters To Display */}
          <div className="form-group">
            <label className="form-label">Parameters to Display</label>
            <div className="form-dropdown-container">
              <button
                type="button"
                className={`form-dropdown-button ${showParametersDropdown ? 'active' : ''}`}
                onClick={() => setShowParametersDropdown(!showParametersDropdown)}
                disabled={isSubmitting}
              >
                <span className="form-dropdown-text">
                  {formData.parameters.length > 0 
                    ? `${formData.parameters.length} parameter${formData.parameters.length > 1 ? 's' : ''} selected`
                    : <span className='text-[#818C95]'>Select Parameters</span>
                  }
                </span>
                <ChevronDown className="w-5 h-5 text-[var(--color-text)]" />
              </button>
              {showParametersDropdown && (
                <>
                  {screenSize <= 768 && <div className="dropdown-overlay" onClick={() => setShowParametersDropdown(false)}></div>}
                  <div className={`dropdown-menu parameters-dropdown-menu ${screenSize <= 768 ? 'dropdown-menu-mobile' : ''}`}>
                    {screenSize <= 768 && (
                      <div className="mobile-modal-header">
                        <h3 className="mobile-modal-title">Select Parameters</h3>
                        <button
                          type="button"
                          className="dropdown-menu-close"
                          onClick={() => setShowParametersDropdown(false)}
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                    <div className="dropdown-menu-content">
                      {devicesLoading ? (
                        <div className="dropdown-loading">Loading parameters...</div>
                      ) : parametersForDashboard.length === 0 ? (
                        <div className="dropdown-empty">No parameters available</div>
                      ) : (
                        <>
                          {/* Select All Option */}
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={handleSelectAllParameters}
                          >
                            <div className="dropdown-item-content">
                              <div className={`dropdown-checkbox ${allParametersSelected ? 'checked' : ''}`}>
                                {allParametersSelected && (
                                  <Check className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <div className="dropdown-item-text">
                                <span className="dropdown-item-name">Select All</span>
                              </div>
                            </div>
                          </button>
                          {parametersForDashboard
                            .filter(parameter => parameter.parameterName.toLowerCase() !== 'aqi')
                            .map((parameter) => {
                          const paramIdStr = parameter.driverID.toString();
                          return (
                            <button
                              key={parameter.driverID}
                              type="button"
                              className="dropdown-item"
                              onClick={() => handleParameterToggle(paramIdStr)}
                            >
                              <div className="dropdown-item-content">
                                <div className={`dropdown-checkbox ${formData.parameters.includes(paramIdStr) ? 'checked' : ''}`}>
                                  {formData.parameters.includes(paramIdStr) && (
                                    <Check className="w-3 h-3 text-white" />
                                  )}
                                </div>
                                <div className="dropdown-item-text">
                                  <span className="dropdown-item-name">{parameter.parameterName} <span className="dropdown-item-dimensions">{parameter.unit}</span></span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

        </form>

        {/* Action Buttons */}
        <div className="modal-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-button secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="modal-button primary"
            disabled={isSubmitting || devicesLoading || formData.deviceIds.length === 0}
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Updating...
              </div>
            ) : (
              'Update'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditShareLinkModal;
